import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check, ChevronDown, Copy, Loader2, Send, X, AlertCircle, ArrowRight, Inbox,
} from 'lucide-react';
import { fetchAllPages } from '../api';
import { sendExecutiveBriefToTeam } from '../utils/executiveBrief';
import {
  getBriefPrefs, getEmailRecipients, previewBrief, saveBriefPrefs, saveEmailRecipients, saveScheduledReports,
} from '../utils/emailActivity';
import CompanyLogo from './CompanyLogo';
import { getRiskLevel } from '../utils/risk';
import { dateFilterToApiParams } from '../utils/format';

// Only used to open a story from the preview: the brief itself is built by the server.
const TODAY_FILTER = { preset: 'today' };
const WINDOW_KEY = 'corpintel_brief_window';
// A real backend job: the server reads this scheduled_reports row every minute, so a
// change here is what actually decides when the email goes out.
const BRIEFS = [
  { id: 'daily-morning', title: 'Daily brief', hint: 'Everything from the last 24 hours' },
];
// Windows for the manual preview / Send now.
const WINDOWS = [
  { key: 'last_24h', label: 'Last 24 hours', hint: 'published in the last 24 hours' },
  { key: 'today', label: 'Today so far', hint: 'published today, IST' },
];
const PREFS_KEY = 'corpintel_daily_brief_prefs';

const DAYS = [
  ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun'],
];
const INCLUDE = [
  ['high', 'High risk only'],
  ['high_medium', 'High and medium risk'],
  ['all', 'All stories'],
];
const LEVELS = {
  high: { label: 'High', hint: 'Act today', color: '#bd2f2b', bg: '#fae8e7' },
  medium: { label: 'Medium', hint: 'Review today', color: '#86690c', bg: '#f7f0d2' },
  low: { label: 'Low', hint: 'For awareness', color: '#117049', bg: '#e1f3ea' },
  unknown: { label: 'Unrated', hint: 'For awareness', color: '#4b6679', bg: '#e6edf2' },
};
const RESULT_TONE = {
  ok: 'text-[#117049]',
  warn: 'text-[#b05a0a]',
  error: 'text-[#bd2f2b]',
};

const CARD = 'bg-white border border-[#e1e5ec] rounded-lg';
const BTN = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] whitespace-nowrap hover:bg-[#f6f7fa] disabled:opacity-40 disabled:cursor-not-allowed transition';
const BTN_PRI = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md bg-[#1c4ed8] text-[13px] font-medium text-white whitespace-nowrap hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition';

function loadPrefs() {
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    return {
      include: INCLUDE.some(([k]) => k === saved.include) ? saved.include : 'high_medium',
      unverified: Boolean(saved.unverified),
    };
  } catch {
    return { include: 'high_medium', unverified: false };
  }
}

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative flex-none w-9 h-5 rounded-full transition ${checked ? 'bg-[#1c4ed8]' : 'bg-[#cbd3df]'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </button>
  );
}

function scheduleLabel(report) {
  const names = DAYS.filter(([key]) => report.days?.includes(key)).map(([, label]) => label);
  const days = names.length === DAYS.length ? 'Every day' : names.join(', ') || 'No days';
  return `${days} · ${report.time} IST`;
}

function nextSend(report) {
  if (!report?.enabled || !report.days?.length) return null;
  const [h, m] = String(report.time || '08:00').split(':').map(Number);
  const keys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  for (let offset = 0; offset < 8; offset += 1) {
    const when = new Date();
    when.setDate(when.getDate() + offset);
    when.setHours(h || 0, m || 0, 0, 0);
    if (when.getTime() > Date.now() && report.days.includes(keys[when.getDay()])) return when;
  }
  return null;
}

function BriefsView({
  emailActivity = [],
  scheduledReports = [],
  onReportsChange,
  onEmailSent,
  onOpenRecord,
}) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [prefsReady, setPrefsReady] = useState(false);
  // The window the user last picked (Last 24 hours / Today so far) is remembered and used as the default.
  const [windowKey, setWindowKey] = useState(() => {
    try {
      const saved = localStorage.getItem(WINDOW_KEY);
      return WINDOWS.some(w => w.key === saved) ? saved : 'last_24h';
    } catch {
      return 'last_24h';
    }
  });
  useEffect(() => {
    try { localStorage.setItem(WINDOW_KEY, windowKey); } catch { /* storage unavailable */ }
  }, [windowKey]);
  // The brief is built by the server (same code as the scheduled emails), so what
  // is previewed here is exactly what Send now emails.
  const [preview, setPreview] = useState({ status: 'loading', brief: null, subject: '', windowLabel: '', error: '' });
  // Today's records are only used to open a story from the preview.
  const [records, setRecords] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoaded, setRecipientsLoaded] = useState(false);
  const [draft, setDraft] = useState('');
  const [recipientError, setRecipientError] = useState('');
  const [sendState, setSendState] = useState('idle');
  const [confirming, setConfirming] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [scheduleError, setScheduleError] = useState('');

  const schedules = useMemo(
    () => BRIEFS.map(brief => ({ ...brief, report: scheduledReports.find(r => r.id === brief.id) || null })),
    [scheduledReports],
  );
  const windowMeta = WINDOWS.find(w => w.key === windowKey) || WINDOWS[0];

  useEffect(() => {
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* storage unavailable */ }
  }, [prefs]);

  useEffect(() => {
    let cancelled = false;
    getEmailRecipients()
      .then(list => { if (!cancelled) { setRecipients(list); setRecipientsLoaded(true); } })
      .catch(() => { if (!cancelled) setRecipientsLoaded(true); });
    fetchAllPages('/intelligence', dateFilterToApiParams(TODAY_FILTER))
      .then(({ results }) => { if (!cancelled) setRecords(results); })
      .catch(() => { if (!cancelled) setRecords([]); });
    // "What's included" is saved on the server so the scheduled emails follow it too.
    getBriefPrefs()
      .then(saved => {
        if (cancelled) return;
        if (saved && INCLUDE.some(([k]) => k === saved.include)) {
          setPrefs({ include: saved.include, unverified: Boolean(saved.unverified) });
        }
      })
      .catch(() => { /* keep the local choice */ })
      .finally(() => { if (!cancelled) setPrefsReady(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!prefsReady) return;
    saveBriefPrefs(prefs).catch(() => { /* preview still works with the local choice */ });
  }, [prefs, prefsReady]);

  useEffect(() => {
    if (!prefsReady) return undefined;
    let cancelled = false;
    setPreview(p => ({ ...p, status: 'loading', error: '' }));
    setConfirming(false);
    const timer = setTimeout(() => {
      previewBrief({ window: windowKey, include: prefs.include, unverified: prefs.unverified })
        .then(data => {
          if (cancelled) return;
          setPreview({
            status: 'ready', brief: data.brief || null, subject: data.subject || '', windowLabel: data.window_label || '', error: '',
          });
        })
        .catch(err => {
          if (!cancelled) setPreview({ status: 'error', brief: null, subject: '', windowLabel: '', error: err.message || 'Could not build the brief' });
        });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [prefsReady, windowKey, prefs.include, prefs.unverified]);

  const brief = preview.brief;
  const loadingBrief = preview.status === 'loading';

  const items = useMemo(() => [...(brief?.briefItems || [])].sort((a, b) => {
    const rank = { high: 3, medium: 2, low: 1, unknown: 0 };
    return rank[getRiskLevel(b.severity)] - rank[getRiskLevel(a.severity)];
  }), [brief]);

  const groups = useMemo(() => ['high', 'medium', 'low'].map(level => ({
    level,
    items: items.filter(item => (getRiskLevel(item.severity) === 'unknown' ? 'low' : getRiskLevel(item.severity)) === level),
  })).filter(group => group.items.length > 0), [items]);

  const subject = preview.subject || '[Corp Sentinel] Brief';

  const persistSchedule = useCallback(async (reportId, patch) => {
    const current = scheduledReports.find(r => r.id === reportId);
    if (!current) return;
    const updated = { ...current, ...patch };
    const next = scheduledReports.map(r => (r.id === reportId ? { ...updated, schedule: scheduleLabel(updated) } : r));
    setScheduleError('');
    onReportsChange?.(next);
    try {
      const saved = await saveScheduledReports(next);
      if (Array.isArray(saved)) onReportsChange?.(saved);
    } catch (err) {
      setScheduleError(err.message || 'Could not save the schedule');
      onReportsChange?.(scheduledReports);
    }
  }, [scheduledReports, onReportsChange]);

  const toggleDay = (report, key) => {
    const days = report.days?.includes(key) ? report.days.filter(d => d !== key) : [...(report.days || []), key];
    persistSchedule(report.id, { days });
  };

  const persistRecipients = async (next) => {
    const previous = recipients;
    setRecipients(next);
    setRecipientError('');
    try {
      await saveEmailRecipients(next);
    } catch (err) {
      setRecipients(previous);
      setRecipientError(err.message || 'Could not save recipients');
    }
  };

  const addRecipient = () => {
    const email = draft.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setRecipientError('Enter a valid email address');
      return;
    }
    if (recipients.includes(email)) {
      setRecipientError('Already on the list');
      return;
    }
    setDraft('');
    persistRecipients([...recipients, email]);
  };

  const plainText = useMemo(() => [
    subject,
    '',
    items.length ? `${items.length} ${items.length === 1 ? 'story needs' : 'stories need'} attention` : 'No stories need attention.',
    '',
    ...items.flatMap((item, i) => [
      `${i + 1}. ${item.company} (${LEVELS[getRiskLevel(item.severity)]?.label || 'Unrated'})`,
      `   ${item.headline}`,
      item.description ? `   ${item.description}` : null,
      '',
    ].filter(line => line !== null)),
  ].join('\n'), [subject, items]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  const canSend = items.length > 0 && !loadingBrief && sendState !== 'sending'
    && !(recipientsLoaded && recipients.length === 0);

  const handleConfirmSend = async () => {
    if (!canSend) return;
    setConfirming(false);
    setSendState('sending');
    setSendResult(null);
    try {
      // The activity row tells the truth: "skipped" means the server has no email
      // set up, so nothing was delivered.
      const activity = await sendExecutiveBriefToTeam(brief, '', subject);
      const count = activity.recipients?.length || 0;
      if (activity.status === 'sent') {
        setSendState('sent');
        setSendResult({ tone: 'ok', text: `Sent to ${count} ${count === 1 ? 'person' : 'people'}.` });
      } else if (activity.status === 'skipped') {
        setSendState('failed');
        setSendResult({ tone: 'warn', text: `Not sent: ${activity.error || 'email is not set up on the server'}.` });
      } else {
        setSendState('failed');
        setSendResult({ tone: 'error', text: `Not sent: ${activity.error || 'delivery failed'}.` });
      }
      onEmailSent?.(activity);
    } catch (err) {
      setSendState('failed');
      setSendResult({ tone: 'error', text: `Not sent: ${err.message || 'could not reach the server'}.` });
    }
    setTimeout(() => setSendState('idle'), 3000);
  };

  const openItem = (item) => {
    const record = records.find(r => r.id === item.record_id);
    if (record) onOpenRecord?.(record);
  };

  const sent = useMemo(() => emailActivity
    .filter(a => String(a.type || '').toLowerCase().includes('executive'))
    .slice(0, 4), [emailActivity]);

  const nextLabelFor = (report) => {
    if (!report) return 'No schedule configured';
    if (!report.enabled) return 'Paused';
    const when = nextSend(report);
    if (!when) return 'Pick at least one day';
    return `Next: ${when.toLocaleDateString('en-GB', { weekday: 'short' })} at ${report.time || '08:00'}`;
  };
  // Soonest upcoming automatic brief, for the summary strip.
  const soonest = schedules
    .map(({ title, report }) => ({ title, report, when: nextSend(report) }))
    .filter(entry => entry.when)
    .sort((a, b) => a.when - b.when)[0] || null;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-[#0d1524]">Daily brief</h1>
          <p className="mt-1 text-sm text-[#45516a] max-w-[60ch]">
            A short email of what needs attention from the last 24 hours. Check the preview, then send it now or let it go out every morning on schedule.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Brief window" className="inline-flex rounded-md border border-[#cbd3df] overflow-hidden bg-white">
            {WINDOWS.map(w => (
              <button key={w.key} type="button" aria-pressed={windowKey === w.key} onClick={() => setWindowKey(w.key)}
                className={`h-8 px-3 text-[13px] font-medium border-r border-[#cbd3df] last:border-r-0 transition ${
                  windowKey === w.key ? 'bg-[#e8eefe] text-[#1c4ed8]' : 'text-[#45516a] hover:bg-[#f6f7fa]'
                }`}>
                {w.label}
              </button>
            ))}
          </div>
          <button type="button" className={BTN} onClick={handleCopy} disabled={!items.length}>
            {copied ? <><Check size={14} className="text-[#117049]" />Copied</> : <><Copy size={14} />Copy text</>}
          </button>
          <button
            type="button"
            className={BTN_PRI}
            onClick={() => { setSendResult(null); setConfirming(true); }}
            disabled={!canSend}
            title={recipientsLoaded && recipients.length === 0 ? 'Add a recipient first'
              : loadingBrief ? 'Building the brief…'
                : !items.length ? 'Nothing to send in this window' : undefined}
          >
            {sendState === 'sending' ? <><Loader2 size={14} className="animate-spin" />Sending…</>
              : sendState === 'sent' ? <><Check size={14} />Sent</>
                : sendState === 'failed' ? <><AlertCircle size={14} />Not sent</>
                  : <><Send size={14} />Send now</>}
          </button>
        </div>
      </div>

      {confirming && (
        <div role="alertdialog" aria-label="Confirm send" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#c7d2fe] bg-[#e8eefe] px-4 py-3">
          <p className="text-sm text-[#0d1524] min-w-0 break-words">
            Send <b>{items.length} {items.length === 1 ? 'story' : 'stories'}</b> ({windowMeta.label.toLowerCase()}) to{' '}
            <b>{recipients.join(', ')}</b>?
          </p>
          <div className="flex gap-2">
            <button type="button" className={BTN} onClick={() => setConfirming(false)}>Cancel</button>
            <button type="button" className={BTN_PRI} onClick={handleConfirmSend}><Send size={14} />Send</button>
          </div>
        </div>
      )}
      {sendResult && !confirming && (
        <p role="status" className={`mb-4 text-sm font-medium ${RESULT_TONE[sendResult.tone]}`}>{sendResult.text}</p>
      )}

      <div className={`${CARD} mb-4 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#e1e5ec]`}>
        <div className="px-5 py-3.5">
          <small className="block text-[13px] font-medium text-[#45516a]">In this brief</small>
          <b className="block text-[22px] leading-tight font-semibold tracking-[-0.02em] text-[#0d1524]">
            {items.length} {items.length === 1 ? 'story' : 'stories'}
          </b>
          <span className="text-xs text-[#76829a]">{windowMeta.hint}</span>
        </div>
        <div className="px-5 py-3.5">
          <small className="block text-[13px] font-medium text-[#45516a]">Goes to</small>
          <b className={`block text-[22px] leading-tight font-semibold tracking-[-0.02em] ${recipientsLoaded && recipients.length === 0 ? 'text-[#b05a0a]' : 'text-[#0d1524]'}`}>
            {recipients.length} {recipients.length === 1 ? 'person' : 'people'}
          </b>
          {recipientsLoaded && recipients.length === 0 ? (
            <button type="button" className="text-xs font-medium text-[#1c4ed8] hover:underline"
              onClick={() => document.getElementById('brief-recipient-input')?.focus()}>
              Add a recipient to send
            </button>
          ) : (
            <span className="text-xs text-[#76829a]">by email</span>
          )}
        </div>
        <div className="px-5 py-3.5">
          <small className="block text-[13px] font-medium text-[#45516a]">Automatic send</small>
          <b className="block text-[22px] leading-tight font-semibold tracking-[-0.02em] text-[#0d1524]">
            {soonest ? `${soonest.when.toLocaleDateString('en-GB', { weekday: 'short' })} ${soonest.report.time}` : 'Off'}
          </b>
          <span className="text-xs text-[#76829a]">{soonest ? `next: ${soonest.title.toLowerCase()}` : 'turn on under Schedule'}</span>
        </div>
      </div>

      <div className="grid gap-4 items-start grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px]">
        {/* Preview */}
        <article className={`${CARD} overflow-hidden min-w-0`}>
          <div className="px-5 py-3.5 bg-[#f6f7fa] border-b border-[#e1e5ec] text-[13px] leading-relaxed text-[#45516a]">
            <div><b className="font-semibold text-[#0d1524]">Subject:</b> {subject}</div>
            <div className="break-words">
              <b className="font-semibold text-[#0d1524]">To:</b>{' '}
              {recipients.length ? recipients.join(', ') : <span className="text-[#b05a0a]">No recipients yet</span>}
            </div>
            <div><b className="font-semibold text-[#0d1524]">From:</b> CorpSentinel</div>
          </div>

          <div className="px-[30px] py-6">
            <h2 className="text-[26px] leading-tight font-semibold tracking-[-0.01em] text-[#0d1524]">
              {loadingBrief
                ? 'Building your brief'
                : items.length
                ?`${items.length} ${items.length === 1 ? 'story needs' : 'stories need'} attention`
                : 'Nothing needs attention'}
            </h2>
            <p className="mt-1 text-[12.5px] text-[#76829a]">{brief?.dateLabel || preview.windowLabel || '\u00a0'}</p>

            {loadingBrief ? (
              <div className="text-center py-12" role="status">
                <Loader2 size={24} className="mx-auto animate-spin text-[#76829a]" />
                <p className="mt-2 text-sm font-medium text-[#0d1524]">Building the brief…</p>
                <p className="mt-1 text-[13px] text-[#76829a]">The AI is summarising each company. This can take up to a minute.</p>
              </div>
            ) : preview.status === 'error' ? (
              <div className="text-center py-12" role="alert">
                <AlertCircle size={24} className="mx-auto text-[#bd2f2b]" />
                <p className="mt-2 text-sm font-medium text-[#0d1524]">Could not build the brief</p>
                <p className="mt-1 text-[13px] text-[#76829a]">{preview.error}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12">
                <Inbox size={26} className="mx-auto text-[#76829a]" />
                <p className="mt-2 text-sm font-medium text-[#0d1524]">No stories in this window</p>
                <p className="mt-1 text-[13px] text-[#76829a] max-w-sm mx-auto">
                  {prefs.include === 'high'
                    ? 'Only high-risk stories are included. Widen “Include” to see more.'
                    : 'Quiet day, or collection has not picked anything up yet.'}
                </p>
              </div>
            ) : groups.map(group => {
              const meta = LEVELS[group.level];
              return (
                <section key={group.level} className="mt-6">
                  <div className="flex items-center gap-2 text-[12.5px] font-medium">
                    <span className="inline-flex items-center gap-1.5 px-[7px] py-1 rounded text-[11px] leading-none"
                      style={{ color: meta.color, background: meta.bg }}>
                      <i className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />{meta.label}
                    </span>
                    <span className="text-[#5b6780]">{meta.hint}</span>
                  </div>
                  {group.items.map(item => {
                    const category = item.category;
                    const unverified = item.verified === false;
                    return (
                      <div key={`${item.company}-${item.headline}`} className="mt-4 pb-4 border-b border-[#e1e5ec] last:border-b-0">
                        <div className="flex items-center gap-3">
                          <CompanyLogo name={item.company} size="md" />
                          <div className="min-w-0">
                            <div className="text-[19px] leading-tight font-extrabold text-[#0d1524]">{item.company}</div>
                            <div className="text-[13px] text-[#45516a]">
                              {category ? `${String(category).charAt(0).toUpperCase()}${String(category).slice(1)}` : ''}
                              {item.published_label ? `${category ? ' · ' : ''}${item.published_label}` : ''}
                              {unverified && <span className="ml-2 text-[#b05a0a]">Unconfirmed source</span>}
                            </div>
                          </div>
                        </div>
                        <h3 className="mt-3 text-base leading-snug font-bold text-[#0d1524]">{item.headline}</h3>
                        {item.description && (
                          <p className="mt-1.5 text-sm leading-relaxed text-[#2b3750] line-clamp-3">{item.description}</p>
                        )}
                        {records.some(r => r.id === item.record_id) ? (
                          <button type="button" onClick={() => openItem(item)}
                            className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-[#1c4ed8] hover:underline">
                            Open in CorpSentinel<ArrowRight size={12} />
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </section>
              );
            })}
          </div>
        </article>

        {/* Settings */}
        <div className="flex flex-col gap-4 min-w-0">
          <div className={`${CARD} p-[18px]`}>
            <div className="flex items-center justify-between">
              <PanelTitle>Recipients</PanelTitle>
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-[#f6f7fa] border border-[#e1e5ec] text-[#45516a]">{recipients.length}</span>
            </div>
            <p className="mt-1.5 text-[13px] text-[#76829a]">Everyone here gets the brief by email.</p>
            <div className="mt-3 flex flex-wrap gap-1.5 border border-[#cbd3df] rounded-md p-1.5 bg-white focus-within:border-[#1c4ed8]">
              {recipients.map(email => (
                <span key={email} className="inline-flex items-center gap-1 text-[13px] bg-[#f6f7fa] border border-[#e1e5ec] rounded pl-2 pr-1 py-0.5">
                  {email}
                  <button type="button" aria-label={`Remove ${email}`} onClick={() => persistRecipients(recipients.filter(r => r !== email))}
                    className="grid place-items-center text-[#76829a] hover:text-[#bd2f2b]">
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                id="brief-recipient-input"
                value={draft}
                onChange={e => { setDraft(e.target.value); setRecipientError(''); }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addRecipient(); }
                  if (e.key === 'Backspace' && !draft && recipients.length) persistRecipients(recipients.slice(0, -1));
                }}
                onBlur={addRecipient}
                placeholder={recipients.length ? 'Add another email' : 'name@company.com, then press Enter'}
                aria-label="Add recipient"
                className="flex-1 min-w-[140px] h-7 px-1 text-[13px] bg-transparent outline-none placeholder:text-[#76829a]"
              />
            </div>
            {recipientError && <p className="mt-1.5 text-xs text-[#bd2f2b]">{recipientError}</p>}
          </div>

          <div className={`${CARD} p-[18px]`}>
            <PanelTitle>Schedule</PanelTitle>
            <p className="mt-1.5 text-[13px] text-[#76829a]">Time is IST. The server sends this email on this schedule.</p>
            {schedules.map(({ id, title, hint, report }, index) => (
              <div key={id} className={index === 0 ? 'mt-3' : 'mt-4 pt-4 border-t border-[#e1e5ec]'}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <b className="block text-sm font-medium text-[#0d1524]">{title}</b>
                    <small className="block text-[13px] text-[#76829a]">{hint}</small>
                    <small className="block text-[13px] text-[#45516a] mt-0.5">{nextLabelFor(report)}</small>
                  </div>
                  <Toggle checked={Boolean(report?.enabled)} label={`Send ${title.toLowerCase()} automatically`}
                    onChange={(enabled) => persistSchedule(id, { enabled })} />
                </div>

                {report && (
                  <>
                    <label className="block mt-3">
                      <span className="block mb-1.5 text-[13px] font-medium text-[#0d1524]">Send at</span>
                      <input
                        type="time"
                        value={report.time || '08:00'}
                        onChange={e => e.target.value && persistSchedule(id, { time: e.target.value })}
                        className="h-8 w-full text-[13px] text-[#0d1524] bg-white border border-[#cbd3df] rounded-md px-2.5 focus:outline-none focus:border-[#1c4ed8]"
                      />
                    </label>

                    <div className="mt-3">
                      <span className="block mb-1.5 text-[13px] font-medium text-[#0d1524]">On these days</span>
                      <div className="flex flex-wrap gap-1.5">
                        {DAYS.map(([key, label]) => {
                          const on = report.days?.includes(key);
                          return (
                            <button key={key} type="button" aria-pressed={on} onClick={() => toggleDay(report, key)}
                              className={`h-8 px-2.5 rounded-md border text-[13px] font-medium transition ${
                                on ? 'bg-[#1c4ed8] border-[#1c4ed8] text-white' : 'bg-white border-[#cbd3df] text-[#45516a] hover:bg-[#f6f7fa]'
                              }`}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
            {scheduleError && <p className="mt-2 text-xs text-[#bd2f2b]">{scheduleError}</p>}
          </div>

          <div className={`${CARD} p-[18px]`}>
            <PanelTitle>What&rsquo;s included</PanelTitle>
            <div className="mt-3">
              <span className="block mb-1.5 text-[13px] font-medium text-[#0d1524]">Risk levels</span>
              <div className="relative">
                <select
                  value={prefs.include}
                  onChange={e => setPrefs(p => ({ ...p, include: e.target.value }))}
                  className="appearance-none h-8 w-full text-[13px] font-medium text-[#0d1524] bg-white border border-[#cbd3df] rounded-md pl-2.5 pr-7 focus:outline-none focus:border-[#1c4ed8] cursor-pointer"
                  aria-label="Risk levels"
                >
                  {INCLUDE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#76829a]" />
              </div>
            </div>

            <div className="mt-4 flex items-start justify-between gap-3">
              <div>
                <b className="block text-sm font-medium text-[#0d1524]">Unverified sources</b>
                <small className="text-[13px] text-[#76829a]">Include them, labelled &ldquo;Unconfirmed&rdquo;.</small>
              </div>
              <Toggle checked={prefs.unverified} label="Include unverified sources"
                onChange={(unverified) => setPrefs(p => ({ ...p, unverified }))} />
            </div>
            <p className="mt-3.5 pt-3 border-t border-[#e1e5ec] text-xs text-[#76829a]">
              These choices change the preview on the left and what <b className="font-medium text-[#45516a]">Send now</b> sends.
            </p>
          </div>

          <div className={`${CARD} p-[18px]`}>
            <PanelTitle>Sent recently</PanelTitle>
            {sent.length === 0 ? (
              <p className="text-[13px] text-[#76829a] mt-2.5">Nothing sent yet. Briefs you send will show up here.</p>
            ) : (
              <div className="mt-2">
                {sent.map(activity => {
                  const failed = activity.status === 'failed';
                  const count = activity.recipients?.length || 0;
                  return (
                    <div key={activity.id} className="flex items-center justify-between gap-3 py-2.5 border-t border-[#e1e5ec] first:border-t-0">
                      <span className="min-w-0">
                        <b className="block text-sm font-medium text-[#0d1524]">
                          {new Date(activity.createdAt).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                        </b>
                        <small className="text-[13px] text-[#76829a]">
                          {failed ? (activity.error || 'Delivery failed') : `Delivered to ${count} ${count === 1 ? 'person' : 'people'}`}
                        </small>
                      </span>
                      {failed
                        ? <AlertCircle size={15} className="text-[#bd2f2b] flex-none" />
                        : <Check size={15} className="text-[#117049] flex-none" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PanelTitle({ children }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold text-[#0d1524]">
      <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />
      {children}
    </h3>
  );
}

export default BriefsView;
